/**
 * Odbočka v priechode a zvar, ktorý jej patrí.
 *
 * Keď sa vlákno odbočí z priechodu, fyzicky sa prestrihne a zvarí. Technik
 * to chce mať v zozname zvarov — tam sa pozerá pri zváraní a to isté číta
 * dokumentácia aj montážny checklist (ten z odbočiek úlohy nerobí, berie
 * len zvary, splittre a prípojky).
 *
 * Preto sa pri odbočení zvar naozaj pridá a drží sa s odbočkou v páre.
 * Pár sa pozná podľa zdrojového kábla a vlákna — jedno vlákno sa dá
 * zvariť len raz, takže je to jednoznačné.
 */

export interface VlaknoPriechodu {
  fiber_number: number;
  is_tapped?: boolean;
  /** ktorý koniec priechodu sa zvára (keď chýba, berie sa from_cable_id) */
  tap_from_cable_id?: number;
  /** technik zaškrtol, že sa má k odbočke zapísať zvar */
  tap_zapisat_zvar?: boolean;
  tap_to_cable_id?: number;
  tap_to_fiber?: number;
  tap_loss_db?: number;
}

export interface Zvar {
  from_source_type: 'cable' | 'olt_port';
  from_cable_id?: number;
  from_fiber?: number;
  from_olt_port?: number;
  to_cable_id: number;
  to_fiber: number;
  loss_db?: number;
  z_odbocky?: boolean;
  [k: string]: any;
}

/** Index zvaru, ktorý patrí k danému zdrojovému vláknu (alebo -1). */
export const zvarKOdbocke = (
  zvary: Zvar[],
  zdrojKabel?: number,
  zdrojVlakno?: number,
): number => zvary.findIndex(
  (z) => z.from_cable_id === zdrojKabel && z.from_fiber === zdrojVlakno);

/**
 * Vráti zoznam zvarov zosúladený so stavom odbočky:
 *  - zvar vyžiadaný a chýba         → pridá ho,
 *  - odbočka zmenená               → prepíše cieľ a útlm,
 *  - odbočka zrušená alebo zvar odškrtnutý → odoberie ho,
 *  - zvar zapísaný ručne           → nechá tak, ako ho technik napísal.
 */
export const zosuladZvarSOdbockou = (
  zvary: Zvar[],
  zdrojKabel: number,
  zdrojVlakno: number,
  vlakno: VlaknoPriechodu,
): Zvar[] => {
  const zoznam = [...zvary];
  const i = zvarKOdbocke(zoznam, zdrojKabel, zdrojVlakno);

  // Zvar sa zapisuje LEN keď si ho technik vyžiada. Odbočené vlákno môže
  // pokojne ostať voľné — vtedy by zvar v dokumentácii klamal.
  if (!vlakno.is_tapped || !vlakno.tap_zapisat_zvar) {
    return i >= 0 && zoznam[i].z_odbocky ? zoznam.filter((_, j) => j !== i) : zoznam;
  }

  // Odbočka, ktorá ukazuje sama na seba (ten istý kábel a vlákno), znamená
  // „vlákno tu z priechodu vystupuje" — napríklad keď ide do splittra.
  // Zvar z X/n do X/n by nedával zmysel, tak sa nezapisuje.
  if (vlakno.tap_to_cable_id === zdrojKabel && vlakno.tap_to_fiber === zdrojVlakno) {
    return i >= 0 && zoznam[i].z_odbocky ? zoznam.filter((_, j) => j !== i) : zoznam;
  }

  const zapis: Zvar = {
    from_source_type: 'cable',
    from_cable_id: zdrojKabel,
    from_fiber: zdrojVlakno,
    to_cable_id: vlakno.tap_to_cable_id as number,
    to_fiber: vlakno.tap_to_fiber as number,
    loss_db: vlakno.tap_loss_db || 0.1,
    z_odbocky: true,
  };

  if (i >= 0) {
    if (!zoznam[i].z_odbocky) return zoznam;   // ručný zvar sa neprepisuje
    zoznam[i] = { ...zoznam[i], ...zapis };
    return zoznam;
  }
  return [...zoznam, zapis];
};
